import classNames from 'classnames';
import { useState } from 'react';
import React from 'react';
import { Autoplay } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { SwiperOptions } from 'swiper/types';
import Container from '@/components/utilities/container/container';
import ModuleWrapper, {
  ModuleWrapperProps,
} from '@/components/utilities/module-wrapper/module-wrapper';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import HeaderCarouselItem, {
  HeaderCarouselItemProps,
} from './header-carousel-item/header-carousel-item';
import HeaderCarouselNavigation from './header-carousel-navigation/header-carousel-navigation';
import HeaderCarouselSearch from './header-carousel-search/header-carousel-search';
import styles from './header-carousel.module.scss';

export interface HeaderCarouselProps extends ModuleWrapperProps {
  /** Search settings for the search bar */
  searchSettings?: Content.Components['HeaderCarouselModuleDto']['searchSettings'];
  /** Indicates whether to show the search bar */
  showSearch?: Content.Components['HeaderCarouselModuleDto']['showSearch'];
  /** The slides to show */
  slides: Content.Components['HeaderCarouselModuleDto']['slides'];
}

const SWIPER_CONFIGURATION: SwiperOptions = {
  autoplay: { delay: 5000, disableOnInteraction: true },
  slidesPerGroup: 1,
  slidesPerView: 1,
  spaceBetween: 0,
  speed: 500,
};

const HeaderCarousel = ({
  searchSettings,
  showSearch = false,
  slides = [],
  ...rest
}: HeaderCarouselProps) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);

  return (
    <React.Fragment>
      <ModuleWrapper
        {...rest}
        className={classNames(styles.component, {
          [styles.hasSearchBar]: showSearch,
        })}
        padding="none"
      >
        <div className={styles.wrapper}>
          <Swiper
            {...SWIPER_CONFIGURATION}
            className={styles.swiper}
            loop={slides.length > 1}
            modules={[Autoplay]}
            onSlideChangeTransitionStart={slide => {
              setCurrentIndex(slide.realIndex);
            }}
          >
            {slides.map((slide, slideIndex) => (
              <SwiperSlide key={slideIndex} className={styles.slide}>
                <HeaderCarouselItem
                  {...slide}
                  currentIndex={currentIndex}
                  hasSearchBar={showSearch}
                  isPlaying={currentIndex === slideIndex && isPlaying}
                  isPrimary={currentIndex === 0}
                  pageCount={slides.length}
                />
              </SwiperSlide>
            ))}

            {slides.length > 1 && (
              <div className={styles.controls}>
                <Container className={styles.container}>
                  <HeaderCarouselNavigation
                    currentIndex={currentIndex}
                    isPlaying={isPlaying}
                    onPause={() => setIsPlaying(false)}
                    onPlay={() => setIsPlaying(true)}
                    pageCount={slides.length}
                  />
                </Container>
              </div>
            )}
          </Swiper>
        </div>
      </ModuleWrapper>

      {searchSettings && showSearch && (
        <HeaderCarouselSearch
          searchSettings={
            searchSettings as Content.Components['SearchSettingsDto']
          }
        />
      )}
    </React.Fragment>
  );
};

HeaderCarousel.displayName = 'HeaderCarousel';

export default HeaderCarousel;
